import { z } from "zod";
import { CapacitorHttp, HttpOptions, HttpResponse } from '@capacitor/core';
import { storageService } from '@/lib/storage';
import { isJwtSessionEnded } from '@/lib/jwtSession';
import { emitSessionInvalidation } from '@/lib/sessionInvalidation';
import { extractErrorMessage } from './utils/errors';
import { logger } from './logger';

// External API base URL - this should be configurable via environment variables
const EXTERNAL_API_BASE = import.meta.env.VITE_EXTERNAL_API_BASE || "https://camilla-dev.eu.ngrok.io";

// Detect if we're running on a native device
const isNative = () => {
  return typeof window !== 'undefined' && (window as any).Capacitor?.isNative;
};

// On web, use relative URLs to go through the Vite proxy
// On native, use absolute URLs to go directly to the API
const getBaseUrl = () => {
  if (isNative()) {
    return EXTERNAL_API_BASE;
  } else {
    return ''; // Use relative URLs on web
  }
};

// API configuration initialized

export class ApiError extends Error {
  /** Parsed response body when available (e.g. { error: { code, message } }). */
  public readonly responseBody?: unknown;

  constructor(
    public status: number,
    public statusText: string,
    message?: string,
    responseBody?: unknown
  ) {
    super(message || `${status}: ${statusText}`);
    this.name = "ApiError";
    this.responseBody = responseBody;
  }
}

export class ValidationError extends Error {
  constructor(message: string, public errors: z.ZodError) {
    super(message);
    this.name = "ValidationError";
  }
}

class ApiClient {
  private nextRequestId(): string {
    return `req-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
  }

  private async getAuthToken(): Promise<string | null> {
    const token = await storageService.getAuthToken();
    if (!token) {
      logger.info("api.authToken.missing");
      return null;
    }
    if (isJwtSessionEnded(token)) {
      logger.warn("api.authToken.ended");
      emitSessionInvalidation("jwt_expired");
      return null;
    }
    logger.debug("api.authToken.active", {
      token_len: token.length,
      token_preview: `${token.slice(0, 10)}...`,
    });
    return token;
  }

  private async getHeaders(): Promise<Record<string, string>> {
    const headers: Record<string, string> = {
      "Content-Type": "application/json",
      "ngrok-skip-browser-warning": "1",
    };

    const token = await this.getAuthToken();
    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }
    logger.debug("api.headers.ready", { hasAuth: !!token });

    return headers;
  }

  private async handleCapacitorResponse(
    response: HttpResponse,
    hadAuth: boolean,
    requestId: string,
    method: string,
    endpoint: string
  ): Promise<any> {
    logger.info("api.response.capacitor", {
      requestId,
      method,
      endpoint,
      status: response.status,
      url: response.url,
      hadAuth,
    });
    if (response.status < 200 || response.status >= 300) {
      if (response.status === 401 && hadAuth) {
        emitSessionInvalidation("api_401");
      }
      logger.error("api.response.capacitor.error", {
        requestId,
        method,
        endpoint,
        status: response.status,
        url: response.url,
        data: response.data,
      });
      const errorData = response.data;
      const parsedBody = typeof errorData === 'string' ? (() => { try { return JSON.parse(errorData); } catch { return undefined; } })() : errorData;
      const errorMessage = extractErrorMessage(errorData);
      throw new ApiError(response.status, 'HTTP Error', errorMessage, parsedBody);
    }

    return response.data;
  }

  private async handleFetchResponse(
    response: Response,
    hadAuth: boolean,
    requestId: string,
    method: string,
    endpoint: string
  ): Promise<any> {
    logger.info("api.response.fetch", {
      requestId,
      method,
      endpoint,
      status: response.status,
      hadAuth,
      url: response.url,
    });
    if (!response.ok) {
      if (response.status === 401 && hadAuth) {
        emitSessionInvalidation("api_401");
      }
      const text = await response.text();
      logger.error("api.response.fetch.error", {
        requestId,
        method,
        endpoint,
        status: response.status,
        statusText: response.statusText,
        url: response.url,
        text_preview: text.substring(0, 200),
      });
      if (response.status === 0 || response.statusText === '') {
        throw new ApiError(0, 'CORS Error', 'CORS policy blocked the request. Please check your API configuration.');
      }
      let parsedBody: unknown;
      try {
        parsedBody = text ? JSON.parse(text) : undefined;
      } catch {
        parsedBody = undefined;
      }
      const errorMessage = extractErrorMessage(text);
      throw new ApiError(response.status, response.statusText, errorMessage, parsedBody);
    }

    const contentType = response.headers.get("content-type");
    if (contentType?.includes("application/json")) {
      return response.json();
    }

    return response.text();
  }

  async get(endpoint: string): Promise<any> {
    const requestId = this.nextRequestId();
    const url = `${getBaseUrl()}${endpoint}`;
    
    try {
      const headers = await this.getHeaders();
      const hadAuth = !!headers.Authorization;
      logger.info("api.request.start", {
        requestId,
        method: "GET",
        endpoint,
        url,
        hadAuth,
      });

      if (isNative()) {
        // Use CapacitorHttp on native devices
        const options: HttpOptions = {
          url,
          method: 'GET',
          headers,
        };

        const response = await CapacitorHttp.get(options);
        return this.handleCapacitorResponse(response, hadAuth, requestId, "GET", endpoint);
      } else {
        // Use fetch on web
        const response = await fetch(url, {
          method: "GET",
          headers,
          mode: 'cors',
        });
        return this.handleFetchResponse(response, hadAuth, requestId, "GET", endpoint);
      }
    } catch (error) {
      logger.error("api.request.failed", {
        requestId,
        method: "GET",
        endpoint,
        error,
      });
      throw error;
    }
  }

  async post(endpoint: string, data?: any): Promise<any> {
    const requestId = this.nextRequestId();
    const url = `${getBaseUrl()}${endpoint}`;
    
    try {
      const headers = await this.getHeaders();
      const hadAuth = !!headers.Authorization;
      logger.info("api.request.start", {
        requestId,
        method: "POST",
        endpoint,
        url,
        hadAuth,
        payload: data,
      });

      if (isNative()) {
        // Use CapacitorHttp on native devices
        const options: HttpOptions = {
          url,
          method: 'POST',
          headers,
          data: data || undefined,
        };

        const response = await CapacitorHttp.post(options);
        return this.handleCapacitorResponse(response, hadAuth, requestId, "POST", endpoint);
      } else {
        // Use fetch on web
        const response = await fetch(url, {
          method: "POST",
          headers,
          body: data ? JSON.stringify(data) : undefined,
          mode: 'cors',
        });
        return this.handleFetchResponse(response, hadAuth, requestId, "POST", endpoint);
      }
    } catch (error) {
      logger.error("api.request.failed", {
        requestId,
        method: "POST",
        endpoint,
        error,
      });
      throw error;
    }
  }

  async put(endpoint: string, data?: any): Promise<any> {
    const requestId = this.nextRequestId();
    const url = `${getBaseUrl()}${endpoint}`;
    
    try {
      const headers = await this.getHeaders();
      const hadAuth = !!headers.Authorization;
      logger.info("api.request.start", {
        requestId,
        method: "PUT",
        endpoint,
        url,
        hadAuth,
        payload: data,
      });

      if (isNative()) {
        // Use CapacitorHttp on native devices
        const options: HttpOptions = {
          url,
          method: 'PUT',
          headers,
          data: data || undefined,
        };

        const response = await CapacitorHttp.put(options);
        return this.handleCapacitorResponse(response, hadAuth, requestId, "PUT", endpoint);
      } else {
        // Use fetch on web
        const response = await fetch(url, {
          method: "PUT",
          headers,
          body: data ? JSON.stringify(data) : undefined,
          mode: 'cors',
        });
        return this.handleFetchResponse(response, hadAuth, requestId, "PUT", endpoint);
      }
    } catch (error) {
      logger.error("api.request.failed", {
        requestId,
        method: "PUT",
        endpoint,
        error,
      });
      throw error;
    }
  }

  async patch(endpoint: string, data?: any): Promise<any> {
    const requestId = this.nextRequestId();
    const url = `${getBaseUrl()}${endpoint}`;
    
    try {
      const headers = await this.getHeaders();
      const hadAuth = !!headers.Authorization;
      logger.info("api.request.start", {
        requestId,
        method: "PATCH",
        endpoint,
        url,
        hadAuth,
        payload: data,
      });

      if (isNative()) {
        // Use CapacitorHttp on native devices
        const options: HttpOptions = {
          url,
          method: 'PATCH',
          headers,
          data: data || undefined,
        };

        // Use request method for PATCH as it may not have a direct patch method
        const response = await CapacitorHttp.request(options);
        return this.handleCapacitorResponse(response, hadAuth, requestId, "PATCH", endpoint);
      } else {
        // Use fetch on web
        const response = await fetch(url, {
          method: "PATCH",
          headers,
          body: data ? JSON.stringify(data) : undefined,
          mode: 'cors',
        });
        return this.handleFetchResponse(response, hadAuth, requestId, "PATCH", endpoint);
      }
    } catch (error) {
      logger.error("api.request.failed", {
        requestId,
        method: "PATCH",
        endpoint,
        error,
      });
      throw error;
    }
  }

  async delete(endpoint: string): Promise<any> {
    const requestId = this.nextRequestId();
    const url = `${getBaseUrl()}${endpoint}`;
    
    try {
      const headers = await this.getHeaders();
      const hadAuth = !!headers.Authorization;
      logger.info("api.request.start", {
        requestId,
        method: "DELETE",
        endpoint,
        url,
        hadAuth,
      });

      if (isNative()) {
        // Use CapacitorHttp on native devices
        const options: HttpOptions = {
          url,
          method: 'DELETE',
          headers,
        };

        const response = await CapacitorHttp.delete(options);
        return this.handleCapacitorResponse(response, hadAuth, requestId, "DELETE", endpoint);
      } else {
        // Use fetch on web
        const response = await fetch(url, {
          method: "DELETE",
          headers,
          mode: 'cors',
        });
        return this.handleFetchResponse(response, hadAuth, requestId, "DELETE", endpoint);
      }
    } catch (error) {
      logger.error("api.request.failed", {
        requestId,
        method: "DELETE",
        endpoint,
        error,
      });
      throw error;
    }
  }

  // Validation helper with better error reporting
  validateResponse<T>(data: any, schema: z.ZodSchema<T>): T {
    try {
      return schema.parse(data);
    } catch (error) {
      if (error instanceof z.ZodError) {
        logger.error("api.validation.failed", {
          errors: error.errors,
          receivedData: data,
          schema: schema.description || "Unknown schema"
        });
        throw new ValidationError("Response validation failed", error);
      }
      throw error;
    }
  }
}

export const apiClient = new ApiClient();
